/**
 * Stock Register (formerly Inventory)
 *
 * Round 4 P6 replacement: the old page read from db.inventory_items which was
 * never actually populated. This one reads /stores/stock-register which
 * aggregates the real sources: stock_layers (project FIFO), opening_stock
 * (godown), material_transfers (in-transit) and reserved MRs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Package, AlertTriangle, Download, Search, Warehouse, TrendingUp, Truck, Layers } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { apiGet } from '../lib/api';
import { inr, inrCompact, formatDate } from '../lib/format';
import { toast } from 'sonner';

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none';

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (projectId) params.project_id = projectId;
      if (category) params.category = category;
      if (q.trim()) params.q = q.trim();
      if (includeZero) params.include_zero = true;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await apiGet('/stores/stock-register', params);
      setItems(r.items || []);
      setTotals(r.totals || {});
      setPageMeta({ total: r.total || 0, pages: r.pages || 1 });
    } catch { toast.error('Stock register load failed'); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, category, includeZero, dateFrom, dateTo, page]);

  useEffect(() => {
    apiGet('/projects').then((d) => setProjects(Array.isArray(d) ? d : d?.items || [])).catch(() => {});
    // Distinct material categories — cheap enough to compute client-side from the register itself
  }, []);

  useEffect(() => {
    const cats = [...new Set(items.map((r) => r.category).filter(Boolean))].sort();
    if (cats.length && !categories.length) setCategories(cats);
  }, [items, categories.length]);

  const download = async () => {
    try {
      const tkn = localStorage.getItem('access_token');
      const qs = new URLSearchParams();
      if (projectId) qs.set('project_id', projectId);
      if (category) qs.set('category', category);
      if (q.trim()) qs.set('q', q.trim());
      if (includeZero) qs.set('include_zero', 'true');
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/stores/stock-register/export?${qs.toString()}`;
      const res = await fetch(url, { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {} });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `stock_register_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(dl);
    } catch { toast.error('Excel export failed'); }
  };

  const onSearchSubmit = (e) => { e.preventDefault(); setPage(1); load(); };

  const totalOnHand = useMemo(
    () => items.reduce((s, r) => s + (Number(r.on_hand) || 0), 0),
    [items],
  );

  return (
    <div>
      <PageHeader
        overline="Stock Register"
        title="Live Material Stock"
        subtitle="Project layers · Godown · In-transit · Reserved — aggregated from stock_layers, opening_stock and material_transfers"
        actions={
          <button
            onClick={download}
            data-testid="stock-register-export"
            className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] transition-all"
          ><Download size={15} /> Export Excel</button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={Layers} label="Distinct Materials" value={totals.distinct_materials ?? '—'} testid="sr-kpi-materials" />
        <Stat icon={Warehouse} label="Total Stock Value" value={inrCompact(totals.total_value || 0)} testid="sr-kpi-value" />
        <Stat icon={Truck} label="In-Transit Value" value={inrCompact(totals.total_in_transit_value || 0)} testid="sr-kpi-in-transit" />
        <Stat icon={TrendingUp} label="Reserved (open MRs) Value" value={inrCompact(totals.total_reserved_value || 0)} testid="sr-kpi-reserved" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-end gap-3">
        <form onSubmit={onSearchSubmit} className="flex-1 min-w-[220px]">
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="sr-search-input"
              placeholder="Material name / code"
              className={`${inputCls} pl-7`}
            />
          </div>
        </form>
        <div className="min-w-[200px]">
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setPage(1); }}
            data-testid="sr-project-filter"
            className={inputCls}
          >
            <option value="">All Projects + Godown</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.code}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            data-testid="sr-category-filter"
            className={inputCls}
          >
            <option value="">All</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-600 cursor-pointer" data-testid="sr-include-zero">
          <input
            type="checkbox"
            checked={includeZero}
            onChange={(e) => { setIncludeZero(e.target.checked); setPage(1); }}
            className="accent-[#3b4872]"
          />
          Include zero-qty layers
        </label>
        <div className="min-w-[140px]">
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Received From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            data-testid="sr-date-from"
            className={inputCls}
          />
        </div>
        <div className="min-w-[140px]">
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Received To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            data-testid="sr-date-to"
            className={inputCls}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table" data-testid="stock-register-table">
          <thead>
            <tr>
              <th>Material</th>
              <th>Code</th>
              <th>Category</th>
              <th>Location</th>
              <th className="text-right">On Hand</th>
              <th className="text-right">Reserved</th>
              <th className="text-right">In-Transit</th>
              <th className="text-right">Available</th>
              <th className="text-right">W-Avg Rate</th>
              <th className="text-right">Value</th>
              <th>Last Move</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="11" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading stock register…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="11" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">
                {q || projectId || category ? 'No stock matches these filters' : 'No stock on hand — receive material via GRN to see it here'}
              </td></tr>
            )}
            {!loading && items.map((r, i) => {
              const available = Math.max(0, (Number(r.on_hand) || 0) - (Number(r.reserved) || 0));
              return (
                <tr key={`${r.source || ''}-${r.location_type || ''}-${r.location_id || ''}-${r.material_id || i}`} data-testid={`sr-row-${r.material_code || i}`}>
                  <td className="max-w-[260px] truncate" title={r.material_name}>{r.material_name}</td>
                  <td className="font-mono text-xs text-slate-500">{r.material_code || '—'}</td>
                  <td className="text-xs">{r.category || '—'}</td>
                  <td>
                    <span className={`text-xs ${r.location_type === 'godown' ? 'text-emerald-700 font-semibold' : ''}`}>
                      {r.location_type === 'godown' ? '🏭 ' : ''}{r.location_name}
                    </span>
                  </td>
                  <td className="text-right tabular-nums font-display font-bold">{Number(r.on_hand).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="text-right tabular-nums text-amber-700">{Number(r.reserved || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="text-right tabular-nums text-slate-500">{Number(r.in_transit || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="text-right tabular-nums font-semibold">{available.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="text-right tabular-nums font-mono text-xs">{inr(r.wavg_rate || 0)}</td>
                  <td className="text-right tabular-nums font-mono text-xs">{inrCompact(r.value || 0)}</td>
                  <td className="text-xs text-slate-500">{r.last_move ? formatDate(r.last_move) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 font-display font-bold">
                <td colSpan="4">Totals (this page)</td>
                <td className="text-right tabular-nums">{totalOnHand.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                <td colSpan="4"></td>
                <td className="text-right tabular-nums font-mono">{inrCompact(items.reduce((s, r) => s + (r.value || 0), 0))}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {pageMeta.pages > 1 && (
        <div className="mt-3">
          <Pagination page={page} pages={pageMeta.pages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="kpi-overline">{label}</div>
      {Icon && <Icon size={13} className="text-slate-400" />}
    </div>
    <div className="font-display text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);
